import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {AnalyticsFactInput, SessionAnalyticsFact} from '../../../models/vte';
import {
    ANALYTICS_FACT_NOT_ENABLED,
    isVerificationFact,
    resolveAnalyticsFactValue,
    resolveSessionAnalyticsFact,
    ResolvedAnalyticsFact,
} from '../analytics-fact.utils';

@Component({
    selector: 'app-analytics-fact-renderer',
    standalone: true,
    imports: [CommonModule],
    template: `
        <ng-container *ngIf="sessionView as session">
            <div class="session-analytics-cell" *ngIf="session.overall as overall">
                <span class="analytics-fact" [ngClass]="factClasses(overall)">
                    <span class="analytics-fact__value">{{ overall.displayValue }}</span>
                    <span *ngIf="overall.confidenceLabel" class="analytics-fact__confidence">
                        {{ overall.confidenceLabel }}
                    </span>
                </span>
                <span *ngIf="session.entry as entry" class="session-analytics-leg">
                    Entry • {{ entry.displayValue }}
                </span>
                <span *ngIf="session.exit as exit" class="session-analytics-leg">
                    Exit • {{ exit.displayValue }}
                </span>
            </div>
        </ng-container>

        <ng-container *ngIf="!sessionView && resolved as fact">
            <span class="analytics-fact" [ngClass]="factClasses(fact)">
                <span class="analytics-fact__value">{{ fact.displayValue }}</span>
                <span *ngIf="fact.confidenceLabel" class="analytics-fact__confidence">
                    {{ fact.confidenceLabel }}
                </span>
            </span>
        </ng-container>
    `,
    styles: [`
        .session-analytics-cell {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            line-height: 1.2;
            gap: 2px;
        }

        .session-analytics-leg {
            font-size: 11px;
            font-weight: 500;
            color: #9ca3af;
            line-height: 1.1;
            white-space: nowrap;
        }

        .analytics-fact {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            font-size: 0.85rem;
            line-height: 1.2;
        }

        .analytics-fact__value {
            font-weight: 600;
        }

        .analytics-fact__confidence {
            font-size: 0.72rem;
            color: #6B7280;
            font-weight: 500;
        }

        .analytics-fact--passed .analytics-fact__value { color: #16A34A; }
        .analytics-fact--failed .analytics-fact__value { color: #DC2626; }
        .analytics-fact--pending .analytics-fact__value { color: #B45309; }
        .analytics-fact--na .analytics-fact__value { color: #6B7280; font-weight: 500; }
    `],
})
export class AnalyticsFactRendererComponent {
    readonly notEnabledLabel = ANALYTICS_FACT_NOT_ENABLED;

    @Input() analyticsKey = '';
    @Input() outputFact = '';
    @Input() label = '';
    @Input() value: AnalyticsFactInput['value'] = null;
    @Input() confidence: number | null = null;
    @Input() fact: AnalyticsFactInput | null = null;
    /**
     * Completed Dashboard session analytics DTO for this output_fact.
     * When set, renderer is presentation-only (no provider_metadata inspection).
     */
    @Input() sessionFact: SessionAnalyticsFact | null = null;
    /** Customer lists show Verification Pending when a gated capability has no fact yet. */
    @Input() showPendingWhenEmpty = true;
    /**
     * When false, empty verification cells show Not Enabled (historical / never expected).
     * When true, empty verification may show Verification Pending.
     */
    @Input() capabilityExpected: boolean | null = null;

    get sessionView() {
        if (!this.sessionFact) {
            return null;
        }
        return resolveSessionAnalyticsFact(
            this.sessionFact,
            this.sessionFact.analytics_key || this.analyticsKey,
            this.outputFact,
        );
    }

    get resolved() {
        const input = this.fact ?? {
            analytics_key: this.analyticsKey,
            output_fact: this.outputFact,
            label: this.label,
            value: this.value,
            confidence: this.confidence,
        };
        if (!input.output_fact?.trim()) {
            return null;
        }
        const analyticsKey = input.analytics_key || this.analyticsKey;
        const outputFact = input.output_fact || this.outputFact;
        const expected = input.capabilityExpected ?? this.capabilityExpected;
        return resolveAnalyticsFactValue(input.value, input.confidence ?? null, {
            analyticsKey,
            outputFact,
            showPendingWhenEmpty: this.showPendingWhenEmpty
                && isVerificationFact(analyticsKey, outputFact),
            capabilityExpected: expected,
        });
    }

    factClasses(fact: ResolvedAnalyticsFact): Record<string, boolean> {
        return {
            'analytics-fact--passed': fact.passed === true,
            'analytics-fact--failed': fact.passed === false,
            'analytics-fact--pending': fact.passed === null && fact.displayValue !== this.notEnabledLabel,
            'analytics-fact--na': fact.displayValue === this.notEnabledLabel,
        };
    }
}
